import { Component, DestroyRef, EventEmitter, inject, Output, signal } from '@angular/core';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { ConfirmModalComponent } from "@shared/components/commons/confirm/confirm-modal.component";
import { IntegrationsApiService } from "@shared/services/integrations-api.service";
import { ToastrService } from "ngx-toastr";
import { catchError, from, map, Observable, of } from "rxjs";
import { ConnectionStatusEnum } from "@models/integrations/connection-status.enum";
import { IntegrationConnectionsEnum } from "@models/integrations/integration-connections.enum";
import { ConnectionResponse } from "@models/integrations/integration-connection-response";
import { getAnnatureConnectionItem } from "../constants/connection-items";
import { IConnection } from "../integration.view.model";
import { AnnatureLoginModalComponent } from "../modals/annature-login-modal/annature-login-modal.component";
import {
  AnnaturePickAccountModalComponent
} from "../modals/annature-pick-account-modal/annature-pick-account-modal.component";
import { AnnatureRegisterModalComponent } from "../modals/annature-register-modal/annature-register-modal.component";

@Component({
  selector: 'app-base-sign-providers',
  standalone: true,
  template: '',
})
export class BaseSignProvidersComponent {
  protected integrationsService = inject(IntegrationsApiService);
  protected modalService = inject(NgbModal);
  protected toastr = inject(ToastrService);
  protected destroyRef = inject(DestroyRef);

  @Output() eSignConnectionChanged = new EventEmitter<void>;

  isLoading = signal(false);
  eSignProviderConnections: IConnection[] = [
    getAnnatureConnectionItem([
      {
        displayStatus: [ConnectionStatusEnum.NotConnected],
        loadingBtn: false,
        disabled: false,
        btnTitle: 'Connect',
        action: (connection: IConnection) => from(this.connect(connection))
      },
      {
        displayStatus: [ConnectionStatusEnum.Connected, ConnectionStatusEnum.ConnectionFailed, ConnectionStatusEnum.WaitForVerification, ConnectionStatusEnum.RequiredAction],
        loadingBtn: false,
        disabled: false,
        btnTitle: 'Disconnect',
        action: (connection: IConnection) => this.disconnect(connection)
      },
      {
        displayStatus: [ConnectionStatusEnum.NotConnected],
        loadingBtn: false,
        disabled: false,
        btnTitle: 'Try for free',
        action: (connection: IConnection) => this.registerAnnatureAccount(connection)
      },
      {
        displayStatus: [ConnectionStatusEnum.WaitForVerification],
        loadingBtn: false,
        disabled: false,
        btnTitle: 'Start eSigning',
        action: (connection: IConnection) => this.checkConnection(connection)
      },
      {
        displayStatus: [ConnectionStatusEnum.WaitForVerification],
        loadingBtn: false,
        disabled: false,
        btnTitle: 'Resend Email',
        action: (connection: IConnection) => this.annatureResendEmail(connection)
      },
      {
        displayStatus: [ConnectionStatusEnum.RequiredAction],
        loadingBtn: false,
        disabled: false,
        btnTitle: 'Complete Login',
        action: (connection: IConnection) => this.completeAnnatureLogin(connection)
      }
    ]),
    // getDocuSignConnectionItem()
  ];

  async connect(connection: IConnection) {
    switch (connection.connectionType) {
      case IntegrationConnectionsEnum.AnnatureConnection:
        this.connectToAnnature(connection);
        break;
    }
  }

  connectToAnnature(connection: IConnection) {
    const modalRef = this.modalService.open(AnnatureLoginModalComponent, { size: 'sm', centered: true });
    const componentInstance = modalRef.componentInstance as AnnatureLoginModalComponent;

    componentInstance.confirmEvent
      .subscribe((status: ConnectionStatusEnum) => {
        if (status === ConnectionStatusEnum.RequiredAction) {
          this.completeAnnatureLogin(connection);
          return;
        }

        this.setConnectionStatus(IntegrationConnectionsEnum.AnnatureConnection, status);
      });
  }

  completeAnnatureLogin(connection: IConnection) {
    const modalRef = this.modalService.open(AnnaturePickAccountModalComponent, {
      size: 'sm',
      centered: true,
      backdrop: 'static'
    });
    const instance = (modalRef.componentInstance as AnnaturePickAccountModalComponent);

    instance.confirmEvent.subscribe((status: ConnectionStatusEnum) => {
      if (status === ConnectionStatusEnum.NotConnected) {
        this.disconnect(connection);
      }

      this.setConnectionStatus(IntegrationConnectionsEnum.AnnatureConnection, status);
    });
  }

  setConnectionStatus(connectionType: IntegrationConnectionsEnum, status: ConnectionStatusEnum) {
    const connectionToUpdate = this.eSignProviderConnections.find((connection) => {
      return connection.connectionType !== undefined && connection.connectionType === connectionType;
    });

    if (connectionToUpdate) {
      connectionToUpdate.connectionStatus = status;
      this.eSignConnectionChanged.emit();
    }
  }

  disconnect(connection: IConnection): void {
    const type = connection.connectionType;

    this.isDisconnectApproved(type).subscribe({
      next: (isApproved) => {
        if (!isApproved) {
          return;
        }
        this.integrationsService.disconnect(type).subscribe({
          next: () => {
            this.setConnectionStatus(type, ConnectionStatusEnum.NotConnected);
            this.toastr.success('Disconnected', 'Success');
          },
          error: () => {
            this.toastr.error('Failed to disconnect', 'Error');
          }
        });
      },
      error: (err) => {
        console.error('Error while confirming disconnect:', err);
      }
    });
  }

  isDisconnectApproved(type: IntegrationConnectionsEnum): Observable<boolean> {
    switch (type) {
      case IntegrationConnectionsEnum.AnnatureConnection: {
        return this.annatureDisconnectMessage();
      }
    }
    return of(true);
  }

  annatureDisconnectMessage(): Observable<boolean> {
    const modalRef = this.modalService.open(ConfirmModalComponent);
    const componentInstance = modalRef.componentInstance as ConfirmModalComponent;

    componentInstance.title = 'Annature Disconnect';
    componentInstance.message = 'All your envelopes will no longer be active.';
    componentInstance.confirmText = 'Disconnect';

    componentInstance.confirm = () => of(true);

    return from(modalRef.result).pipe(
      map(result => {
        return result === true;
      }),
      catchError(() => of(false))
    );
  }

  registerAnnatureAccount(connection: IConnection) {
    const modalRef = this.modalService.open(AnnatureRegisterModalComponent, { size: 'sm', centered: true });
    (modalRef.componentInstance as AnnatureLoginModalComponent).confirmEvent.subscribe((status: ConnectionStatusEnum) => {
      this.setConnectionStatus(IntegrationConnectionsEnum.AnnatureConnection, status);
    });
  }

  checkConnection(connection: IConnection) {
    if (connection == undefined) {
      this.toastr.error("Connection not found", "Error");
      return;
    }

    this.integrationsService.checkConnection(connection.connectionType)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          connection.connectionStatus = response.status;
          connection.lastSync = response.lastSyncDate;
          connection.syncStatus = response.syncStatus;
          this.showResponseToaster(response);
          connection.isLoading = false;
        },
        error: () => {
          connection.connectionStatus = ConnectionStatusEnum.NotConnected;
          connection.isLoading = false;
        }
      });
    connection.isLoading = false;
  }

  annatureResendEmail(connection: IConnection) {
    this.integrationsService.resendVerificationAnnature()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastr.success('Email was sent!', 'Success');
        },
        error: () => {
          this.toastr.error('Failed to send email!', 'Error');
        }
      });
  }

  showResponseToaster(response: ConnectionResponse): void {
    switch (response.status) {
      case ConnectionStatusEnum.RequiredAction:
      case ConnectionStatusEnum.WaitForVerification:
        this.toastr.warning(response.message, 'Required Action');
        break;
      case ConnectionStatusEnum.ConnectionFailed:
      case ConnectionStatusEnum.NotConnected:
        if (response.message)
          this.toastr.error(response.message, 'Error');
        break;
    }
  }
}
